// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: quick event creation from a chat — the condensed form behind the
// assistant's "Create event" suggestion and the message right-click flow.
// Title, the exact times, people suggested from the conversation (one tap to
// add, "everyone in this chat" as a shortcut, search for anyone else) and the
// meeting link. Opens as a popover anchored to the button on desktop and as
// a sheet-style modal on phones. "More options" hands everything to the full
// event form.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {Channel} from '@app/features/channel/models/Channel';
import {CANCEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Activity from '@app/features/konvo_activity/state/Activity';
import {LEFT_REST, RIGHT_REST} from '@app/features/konvo_assistant/components/AssistantThinking';
import {KonvoCreateEventModal} from '@app/features/konvo_calendar/components/KonvoCreateEventModal';
import styles from '@app/features/konvo_calendar/components/KonvoQuickEventCard.module.css';
import {useAttendeeAvailability} from '@app/features/konvo_calendar/hooks/useAttendeeAvailability';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import {isValidEventTimeRange, toLocalInput} from '@app/features/konvo_calendar/utils/KonvoEventForm';
import {defaultVoiceChannel, listVoiceChannelOptions} from '@app/features/konvo_calendar/utils/KonvoVoiceChannels';
import {RecipientField} from '@app/features/konvo_mail/components/RecipientField';
import type {KonvoMailAddress} from '@app/features/konvo_mail/state/KonvoMail';
import {http} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as PopoutCommands from '@app/features/ui/commands/PopoutCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {TeamsLogo} from '@app/features/ui/components/icons/TeamsLogo';
import type {PopoutPosition} from '@app/features/ui/popover';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import * as FormUtils from '@app/lib/forms';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const TITLE_DESCRIPTOR = msg({message: 'Event title', comment: 'Placeholder of the quick event card title.'});
const WHEN_DESCRIPTOR = msg({message: 'When', comment: 'Row label on the quick event card.'});
const PEOPLE_DESCRIPTOR = msg({message: 'People', comment: 'Row label on the quick event card.'});
const LINK_DESCRIPTOR = msg({message: 'Link', comment: 'Row label on the quick event card (meeting link).'});
const ADD_SOMEONE_DESCRIPTOR = msg({
	message: 'Add someone',
	comment: 'Label of the search field on the quick event card that adds attendees by name or email.',
});
const EVERYONE_DESCRIPTOR = msg({
	message: 'Everyone in this chat ({count})',
	comment: 'Chip on the quick event card that adds all chat participants as attendees.',
});
const SUGGESTED_DESCRIPTOR = msg({
	message: 'Suggested from this conversation. You are the organiser.',
	comment: 'Hint under the people chips on the quick event card.',
});
const KONVO_DESCRIPTOR = msg({message: 'Konvo', comment: 'Meeting link choice on the quick event card.'});
const TEAMS_DESCRIPTOR = msg({message: 'Teams', comment: 'Meeting link choice on the quick event card.'});
const NO_LINK_DESCRIPTOR = msg({message: 'None', comment: 'Meeting link choice on the quick event card.'});
const VOICE_CHANNEL_DESCRIPTOR = msg({message: 'Voice channel', comment: 'Combobox label on the quick event card.'});
const MORE_OPTIONS_DESCRIPTOR = msg({
	message: 'More options',
	comment: 'Link on the quick event card that opens the full event form.',
});
const CREATE_DESCRIPTOR = msg({message: 'Create event', comment: 'Confirm button on the quick event card.'});
const CREATE_INVITE_DESCRIPTOR = msg({
	message: 'Create & invite {count}',
	comment: 'Confirm button on the quick event card; count is the number of attendees.',
});
const OUTLOOK_DESCRIPTOR = msg({message: 'Outlook', comment: 'Badge on the quick event card naming the calendar.'});
const CREATED_DESCRIPTOR = msg({
	message: 'Event created in your Outlook calendar.',
	comment: 'Toast after creating an event from the quick event card.',
});
const INVALID_RANGE_DESCRIPTOR = msg({
	message: 'The event must end after it starts.',
	comment: 'Validation on the quick event card.',
});
const ORGANISER_DESCRIPTOR = msg({
	message: 'Add people to send them an invite. You are the organiser.',
	comment: 'Hint under the people field on the quick event card opened by dragging on the calendar.',
});
const ASSISTANT_DESCRIPTOR = msg({message: 'Konvo Assistant', comment: 'Accessible name of the mascot.'});
const NEW_EVENT_DESCRIPTOR = msg({message: 'New event', comment: 'Title of the quick event sheet on phones.'});
const CHECKING_DESCRIPTOR = msg({message: 'Checking calendars…', comment: 'Hint while availability loads.'});
const ALL_FREE_DESCRIPTOR = msg({message: 'Everyone is free at this time.', comment: 'Hint when no attendee clashes.'});

export interface QuickEventPerson {
	name: string;
	address: string;
}

export interface QuickEventPrefill {
	subject: string;
	/** ISO instants. */
	start: string;
	end: string;
	/** Pre-selected attendees. */
	attendees: Array<QuickEventPerson>;
	/** Everyone the reader might want to invite (chat participants), including the pre-selected ones. */
	people: Array<QuickEventPerson>;
	/** Short note about where the proposal came from, e.g. "Ben's open slot". */
	hint?: string | null;
	/** How the time shows on the calendar; an action chip's "Block time" sends busy. */
	showAs?: 'busy';
	/**
	 * Where the card was opened. `calendar` (a drag on the Day / Week grid) has
	 * no conversation behind it: no mascot, the title is focused and Enter creates.
	 */
	origin?: 'calendar';
}

interface KonvoQuickEventCardProps {
	prefill: QuickEventPrefill;
	/** The chat this came from; picks the default Konvo voice channel. */
	channel: Channel | null;
	variant: 'popout' | 'sheet';
	onClose: () => void;
	onCreated?: () => void;
}

type LinkKind = 'konvo' | 'teams' | 'none';

const initials = (name: string): string =>
	name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');

const sameAddress = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();

export const KonvoQuickEventCard = observer(
	({prefill, channel, variant, onClose, onCreated}: KonvoQuickEventCardProps) => {
		const {i18n} = useLingui();
		const [subject, setSubject] = useState(prefill.subject);
		const [start, setStart] = useState(() => toLocalInput(new Date(prefill.start)));
		const [end, setEnd] = useState(() => toLocalInput(new Date(prefill.end)));
		const [attendees, setAttendees] = useState<Array<KonvoMailAddress>>(() =>
			prefill.attendees.map((person) => ({name: person.name, address: person.address})),
		);
		const voiceOptions = listVoiceChannelOptions();
		const defaultVoice = useMemo(
			() => defaultVoiceChannel(voiceOptions, channel?.guildId ?? null),
			// Only pick a default once; the list is re-read each render for the combobox.
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);
		const [link, setLink] = useState<LinkKind>(defaultVoice ? 'konvo' : 'none');
		const [voice, setVoice] = useState(defaultVoice?.value ?? '');
		const [submitting, setSubmitting] = useState(false);
		const validRange = isValidEventTimeRange(start, end, false);
		const selectedVoice = voiceOptions.find((option) => option.value === voice) ?? null;

		const availability = useAttendeeAvailability(i18n, attendees, start, end, validRange);
		const {conflicts, checking, statusFor} = availability;

		const isSelected = useCallback(
			(person: QuickEventPerson) => attendees.some((entry) => sameAddress(entry.address, person.address)),
			[attendees],
		);
		const toggle = useCallback((person: QuickEventPerson) => {
			setAttendees((current) =>
				current.some((entry) => sameAddress(entry.address, person.address))
					? current.filter((entry) => !sameAddress(entry.address, person.address))
					: [...current, {name: person.name, address: person.address}],
			);
		}, []);
		const addEveryone = useCallback(() => {
			setAttendees((current) => [
				...current,
				...prefill.people
					.filter((person) => !current.some((entry) => sameAddress(entry.address, person.address)))
					.map((person) => ({name: person.name, address: person.address})),
			]);
		}, [prefill.people]);
		const everyoneAdded = prefill.people.every(isSelected);
		const fromCalendar = prefill.origin === 'calendar';
		// Dragged out on the calendar: the title is the next thing to type. The popout's focus
		// manager focuses its own box once it is placed, so take focus back a frame after that.
		const titleRef = useRef<HTMLInputElement>(null);
		useEffect(() => {
			if (!fromCalendar) return;
			let frame = requestAnimationFrame(() => {
				frame = requestAnimationFrame(() => titleRef.current?.focus({preventScroll: true}));
			});
			titleRef.current?.focus({preventScroll: true});
			return () => cancelAnimationFrame(frame);
		}, [fromCalendar]);

		const openFullForm = useCallback(() => {
			const initial = {
				subject: subject.trim(),
				start: new Date(start).toISOString(),
				end: new Date(end).toISOString(),
				attendees: attendees.map((entry) => entry.address),
			};
			onClose();
			ModalCommands.push(
				modal(() => (
					<KonvoCreateEventModal
						initial={initial}
						onClose={() => ModalCommands.pop()}
						onCreated={() => {
							onCreated?.();
							void KonvoCalendar.refresh();
						}}
					/>
				)),
			);
		}, [subject, start, end, attendees, prefill.people, onClose, onCreated]);

		const submit = useCallback(async () => {
			if (!subject.trim() || !validRange) return;
			setSubmitting(true);
			try {
				await http.post(Endpoints.KONVO_CALENDAR_EVENTS, {
					body: {
						subject: subject.trim(),
						start,
						end,
						is_all_day: false,
						timezone: KonvoCalendar.timezone,
						location: '',
						body_text: '',
						attendees: attendees.map((entry) => entry.address),
						optional_attendees: [],
						teams: link === 'teams',
						konvo_public: false,
						konvo_channel_id: link === 'konvo' && selectedVoice ? selectedVoice.channelId : null,
						konvo_guild_id: link === 'konvo' && selectedVoice ? selectedVoice.guildId : null,
						konvo_channel_name: link === 'konvo' && selectedVoice ? selectedVoice.label : null,
						recurrence: null,
						...(prefill.showAs ? {show_as: prefill.showAs} : {}),
					},
				});
				ToastCommands.createToast({type: 'success', children: i18n._(CREATED_DESCRIPTOR)});
				Activity.track('action.done', {action: 'event.create', origin: 'konvo'});
				onCreated?.();
				void KonvoCalendar.refresh();
				onClose();
			} catch (error) {
				ToastCommands.createToast({type: 'error', children: FormUtils.extractErrorMessage(i18n, error)});
			} finally {
				setSubmitting(false);
			}
		}, [subject, validRange, start, end, attendees, link, selectedVoice, prefill.showAs, i18n, onCreated, onClose]);

		const startDate = start.slice(0, 10);
		const startTime = start.slice(11, 16);
		const endTime = end.slice(11, 16);
		const setStartParts = (date: string, time: string) => {
			const next = `${date}T${time}`;
			const delta = new Date(next).getTime() - new Date(start).getTime();
			setStart(next);
			// Keep the duration when the start moves.
			if (Number.isFinite(delta)) setEnd(toLocalInput(new Date(new Date(end).getTime() + delta)));
		};

		return (
			<div className={clsx(styles.card, variant === 'sheet' && styles.sheet)} data-flx="konvo-calendar.quick-event">
				<div className={styles.head}>
					{!fromCalendar && (
						<svg
							className={styles.mascot}
							viewBox="-30 -30 572 572"
							role="img"
							aria-label={i18n._(ASSISTANT_DESCRIPTOR)}
						>
							<path fill="#9981F4" d={LEFT_REST} />
							<path fill="#FFA16F" d={RIGHT_REST} />
						</svg>
					)}
					<input
						className={styles.title}
						value={subject}
						placeholder={i18n._(TITLE_DESCRIPTOR)}
						onChange={(event) => setSubject(event.target.value)}
						ref={titleRef}
						onKeyDown={(event) => {
							if (fromCalendar && event.key === 'Enter' && !event.nativeEvent.isComposing) {
								event.preventDefault();
								void submit();
							}
						}}
						aria-label={i18n._(TITLE_DESCRIPTOR)}
						data-flx="konvo-calendar.quick-event.title"
					/>
					<span className={styles.source}>{i18n._(OUTLOOK_DESCRIPTOR)}</span>
				</div>

				<div className={styles.row}>
					<div className={styles.label}>{i18n._(WHEN_DESCRIPTOR)}</div>
					<div>
						<div className={styles.when}>
							<input
								type="date"
								value={startDate}
								onChange={(event) => setStartParts(event.target.value, startTime)}
								aria-label={i18n._(WHEN_DESCRIPTOR)}
								data-flx="konvo-calendar.quick-event.date"
							/>
							<input
								type="time"
								value={startTime}
								onChange={(event) => setStartParts(startDate, event.target.value)}
								data-flx="konvo-calendar.quick-event.start"
							/>
							<span>–</span>
							<input
								type="time"
								value={endTime}
								onChange={(event) => setEnd(`${end.slice(0, 10)}T${event.target.value}`)}
								data-flx="konvo-calendar.quick-event.end"
							/>
							{prefill.hint && <span className={styles.hint}>{prefill.hint}</span>}
						</div>
						{!validRange && <div className={styles.error}>{i18n._(INVALID_RANGE_DESCRIPTOR)}</div>}
					</div>
				</div>

				<div className={styles.row}>
					<div className={styles.label}>{i18n._(PEOPLE_DESCRIPTOR)}</div>
					<div>
						{prefill.people.length > 0 && (
							<div className={styles.chips}>
								{prefill.people.map((person) => {
									const on = isSelected(person);
									return (
										<button
											key={person.address}
											type="button"
											className={clsx(styles.chip, on && styles.on)}
											onClick={() => toggle(person)}
											aria-pressed={on}
											data-flx="konvo-calendar.quick-event.person"
										>
											<span className={styles.avatar}>{initials(person.name)}</span>
											{person.name}
											{on && statusFor(person.address) === 'busy' && <span className={clsx(styles.dot, styles.busy)} />}
											{on && statusFor(person.address) === 'tentative' && (
												<span className={clsx(styles.dot, styles.tentative)} />
											)}
											<span className={styles.mark}>{on ? '✓' : '+'}</span>
										</button>
									);
								})}
								{prefill.people.length > 1 && !everyoneAdded && (
									<button
										type="button"
										className={clsx(styles.chip, styles.all)}
										onClick={addEveryone}
										data-flx="konvo-calendar.quick-event.everyone"
									>
										{i18n._(EVERYONE_DESCRIPTOR, {count: prefill.people.length})}
									</button>
								)}
							</div>
						)}
						<RecipientField label={i18n._(ADD_SOMEONE_DESCRIPTOR)} value={attendees} onChange={setAttendees} />
						{conflicts.length > 0 ? (
							<ul className={styles.conflicts} data-flx="konvo-calendar.quick-event.conflicts">
								{conflicts.map((conflict) => (
									<li
										key={conflict.address}
										className={conflict.status === 'busy' ? styles.busyText : styles.tentativeText}
									>
										{conflict.text}
									</li>
								))}
							</ul>
						) : (
							<div className={styles.hint} style={{marginTop: 6}}>
								{checking
									? i18n._(CHECKING_DESCRIPTOR)
									: attendees.length > 0 && availability.checked
										? i18n._(ALL_FREE_DESCRIPTOR)
										: fromCalendar
											? i18n._(ORGANISER_DESCRIPTOR)
											: i18n._(SUGGESTED_DESCRIPTOR)}
							</div>
						)}
					</div>
				</div>

				<div className={styles.row}>
					<div className={styles.label}>{i18n._(LINK_DESCRIPTOR)}</div>
					<div>
						<div className={styles.segment} role="radiogroup" aria-label={i18n._(LINK_DESCRIPTOR)}>
							<button
								type="button"
								className={clsx(link === 'konvo' && styles.on)}
								disabled={voiceOptions.length === 0}
								onClick={() => setLink('konvo')}
								role="radio"
								aria-checked={link === 'konvo'}
								data-flx="konvo-calendar.quick-event.link-konvo"
							>
								<span className={styles.dot} style={{background: 'var(--brand-primary)'}} />
								{i18n._(KONVO_DESCRIPTOR)}
								{link === 'konvo' && selectedVoice ? ` · ${selectedVoice.channelName}` : ''}
							</button>
							<button
								type="button"
								className={clsx(link === 'teams' && styles.on)}
								onClick={() => setLink('teams')}
								role="radio"
								aria-checked={link === 'teams'}
								data-flx="konvo-calendar.quick-event.link-teams"
							>
								<TeamsLogo size={14} />
								{i18n._(TEAMS_DESCRIPTOR)}
							</button>
							<button
								type="button"
								className={clsx(link === 'none' && styles.on)}
								onClick={() => setLink('none')}
								role="radio"
								aria-checked={link === 'none'}
								data-flx="konvo-calendar.quick-event.link-none"
							>
								{i18n._(NO_LINK_DESCRIPTOR)}
							</button>
						</div>
						{link === 'konvo' && voiceOptions.length > 1 && (
							<div className={styles.channelPick}>
								<Combobox
									label={i18n._(VOICE_CHANNEL_DESCRIPTOR)}
									value={voice}
									options={voiceOptions}
									onChange={(value) => setVoice(value)}
								/>
							</div>
						)}
					</div>
				</div>

				<div className={styles.foot}>
					<button
						type="button"
						className={styles.more}
						onClick={openFullForm}
						data-flx="konvo-calendar.quick-event.more"
					>
						{i18n._(MORE_OPTIONS_DESCRIPTOR)} ›
					</button>
					<span className={styles.spacer} />
					<Button variant="secondary" small onClick={onClose} data-flx="konvo-calendar.quick-event.cancel">
						{i18n._(CANCEL_DESCRIPTOR)}
					</Button>
					<Button
						variant="primary"
						small
						submitting={submitting}
						disabled={!subject.trim() || !validRange}
						onClick={() => void submit()}
						data-flx="konvo-calendar.quick-event.create"
					>
						{attendees.length > 0
							? i18n._(CREATE_INVITE_DESCRIPTOR, {count: attendees.length})
							: i18n._(CREATE_DESCRIPTOR)}
					</Button>
				</div>
			</div>
		);
	},
);

const QuickEventSheet = ({prefill, channel, onClose, onCreated}: Omit<KonvoQuickEventCardProps, 'variant'>) => {
	const {i18n} = useLingui();
	return (
		<Modal.Root size="medium" centered onClose={onClose} data-flx="konvo-calendar.quick-event.sheet">
			<Modal.Header title={i18n._(NEW_EVENT_DESCRIPTOR)} data-flx="konvo-calendar.quick-event.sheet-header" />
			<Modal.Content data-flx="konvo-calendar.quick-event.sheet-content">
				<Modal.ContentLayout data-flx="konvo-calendar.quick-event.sheet-layout">
					<KonvoQuickEventCard
						prefill={prefill}
						channel={channel}
						variant="sheet"
						onClose={onClose}
						onCreated={onCreated}
					/>
				</Modal.ContentLayout>
			</Modal.Content>
		</Modal.Root>
	);
};

/**
 * Opens the quick event card: anchored above `target` on desktop, as a modal
 * sheet on phones or when there is nothing to anchor to. `onClosed` runs once
 * the card goes away, however it was closed.
 */
export function openQuickEvent(params: {
	prefill: QuickEventPrefill;
	channel: Channel | null;
	target?: HTMLElement | null;
	position?: PopoutPosition;
	onCreated?: () => void;
	onClosed?: () => void;
}): void {
	const {prefill, channel, target, position = 'top-end', onCreated, onClosed} = params;
	if (target && !MobileLayout.enabled) {
		const key = `konvo-quick-event-${Date.now()}`;
		PopoutCommands.open({
			key,
			target,
			position,
			offsetMainAxis: 8,
			animationType: 'smooth',
			onClose: onClosed,
			render: ({onClose}) => (
				<KonvoQuickEventCard
					prefill={prefill}
					channel={channel}
					variant="popout"
					onClose={onClose}
					onCreated={onCreated}
				/>
			),
		});
		return;
	}
	ModalCommands.push(
		modal(() => (
			<QuickEventSheet
				prefill={prefill}
				channel={channel}
				onClose={() => {
					ModalCommands.pop();
					onClosed?.();
				}}
				onCreated={onCreated}
			/>
		)),
	);
}
